import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Alert, Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import adminApi from "../api/adminApi";

const EMPTY_FORM = { code: "", brand: "", fuelType: "Petrol", transmission: "Manual", seats: 5, doors: 5, description: "", active: true };

const VehicleDialog = ({ open, onClose, vehicle, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [pictureFile, setPictureFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(vehicle ? { ...vehicle } : EMPTY_FORM);
    setPictureFile(null);
    setError("");
  }, [vehicle, open]);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (k !== "_id" && k !== "__v" && k !== "picture") fd.append(k, v); });
      if (pictureFile) fd.append("picture", pictureFile);
      if (vehicle?._id) {
        await adminApi.put(`/fleet/${vehicle._id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await adminApi.post("/fleet", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{vehicle?._id ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}><TextField label="SIPP Code" fullWidth value={form.code} onChange={set("code")} disabled={!!vehicle?._id} /></Grid>
          <Grid item xs={6}><TextField label="Brand / Model" fullWidth value={form.brand} onChange={set("brand")} /></Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Fuel Type</InputLabel>
              <Select value={form.fuelType} label="Fuel Type" onChange={set("fuelType")}>
                {["Petrol", "Diesel", "Electric", "Hybrid"].map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Transmission</InputLabel>
              <Select value={form.transmission} label="Transmission" onChange={set("transmission")}>
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Automatic">Automatic</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}><TextField label="Seats" type="number" fullWidth value={form.seats} onChange={set("seats")} /></Grid>
          <Grid item xs={3}><TextField label="Doors" type="number" fullWidth value={form.doors} onChange={set("doors")} /></Grid>
          <Grid item xs={6}>
            <Button variant="outlined" component="label" fullWidth sx={{ height: 56 }}>
              {pictureFile ? pictureFile.name : "Upload Image"}
              <input type="file" accept="image/*" hidden onChange={(e) => setPictureFile(e.target.files[0])} />
            </Button>
          </Grid>
          <Grid item xs={12}><TextField label="Description" fullWidth multiline rows={3} value={form.description} onChange={set("description")} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          sx={{ backgroundColor: "#1C2541" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FleetManager = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await adminApi.get("/fleet/all");
    setVehicles(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this vehicle?")) return;
    await adminApi.delete(`/fleet/${id}`);
    load();
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold" color="#1C2541">Fleet Manager</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDialogOpen(true); }}
          sx={{ backgroundColor: "#1C2541" }}>
          Add Vehicle
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
            <TableRow>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Brand / Model</strong></TableCell>
              <TableCell><strong>Fuel</strong></TableCell>
              <TableCell><strong>Transmission</strong></TableCell>
              <TableCell><strong>Seats</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vehicles.map((v) => (
              <TableRow key={v._id} hover>
                <TableCell><Typography variant="body2" fontFamily="monospace">{v.code}</Typography></TableCell>
                <TableCell>{v.brand}</TableCell>
                <TableCell>{v.fuelType}</TableCell>
                <TableCell>{v.transmission}</TableCell>
                <TableCell>{v.seats}</TableCell>
                <TableCell><Chip label={v.active ? "Active" : "Inactive"} color={v.active ? "success" : "default"} size="small" /></TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => { setSelected(v); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(v._id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <VehicleDialog open={dialogOpen} onClose={() => setDialogOpen(false)} vehicle={selected} onSaved={load} />
    </Box>
  );
};

export default FleetManager;
